import React, { useRef } from 'react';
import { textVariantStyleMap } from '../Text';
import { useToggle } from '../../../hooks/useToggle';
import { Pressable, StyleProp, TextInput, View, ViewStyle } from 'react-native';
import { Color, colors } from '../../../utils/theme';
import IconButton from '../IconButton';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import { getSpacing } from '../../../utils/spacing';
import getBrowser from '../../../utils/getBrowser';

export type SearchInputOwnProps = {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    style?: StyleProp<ViewStyle>;
    testID?: string;
    debounceInterval?: number;
    isDisabled?: boolean;
    keepPlaceholderOnFocus?: boolean;
    backgroundColorOverride?: Color;
};

export type SearchInputProps = SearchInputOwnProps;

const SearchInput: React.FC<SearchInputProps> = (props) => {
    const inputRef = useRef<React.LegacyRef<TextInput> | null>(null);
    const isDisabled = useIsDisabledByParent(props.isDisabled);
    const debouncedValueReturnValue = useDebouncedValue({
        debouncedValue: props.value,
        debouncedOnChange: props.onChange,
        debounceInterval: props.debounceInterval || 0,
        isDisabled: props.debounceInterval === undefined,
    });
    const hasFocusToggle = useToggle(false);
    const hasValue = !!debouncedValueReturnValue.value;
    const treatAsUnfocused = props.keepPlaceholderOnFocus && hasFocusToggle.value && !hasValue;
    const usePlaceholderText = ((!hasFocusToggle.value || treatAsUnfocused) && !hasValue) || isDisabled;
    const textStyle = textVariantStyleMap[usePlaceholderText ? 'placeholder-text' : 'input-text'];
    const borderColor: Color = hasFocusToggle.value ? 'Primary-Reg' : hasValue ? 'Dark-Text' : 'Grey-Light';
    const browser = getBrowser(navigator.userAgent);

    return (
        <Pressable
            onPress={() => {
                (inputRef.current as any)?.focus?.();
            }}
            style={props.style}
            disabled={isDisabled}
        >
            <View
                style={[
                    {
                        borderWidth: 1,
                        borderRadius: 5,
                        borderColor: colors[borderColor],
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: getSpacing(4),
                        backgroundColor: isDisabled
                            ? colors.White
                            : colors[props.backgroundColorOverride || 'Background'],
                    },
                ]}
            >
                <TextInput
                    ref={inputRef as any}
                    onFocus={isDisabled ? undefined : hasFocusToggle.toggleOn}
                    onBlur={hasFocusToggle.toggleOff}
                    style={{
                        ...textStyle,
                        flex: 1,
                        borderColor: 'transparent',
                        cursor: isDisabled ? 'not-allowed' : undefined,
                        ...({ outlineStyle: 'none' } as any),
                        ...({ fontSynthesis: browser === 'Safari' ? 'style' : undefined } as any),
                    }}
                    testID={props.testID}
                    value={debouncedValueReturnValue.value}
                    placeholder={hasFocusToggle.value && !props.keepPlaceholderOnFocus ? undefined : props.placeholder}
                    onChangeText={isDisabled ? () => {} : debouncedValueReturnValue.onChange}
                    editable={!isDisabled}
                    selectTextOnFocus={!isDisabled}
                    focusable={!isDisabled}
                />
                <IconButton
                    variant="transparent"
                    icon={hasValue ? 'XCircle' : 'Search'}
                    onPress={(e) => {
                        if (hasValue) {
                            e.preventDefault();
                            debouncedValueReturnValue.onChange('', true);
                        } else {
                            (inputRef.current as any)?.focus?.();
                        }
                    }}
                    isDisabled={isDisabled}
                    testID={`${props.testID}-clear-button`}
                />
            </View>
        </Pressable>
    );
};

export default React.memo(SearchInput);
